import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Wand2, X, Loader2, Sparkles } from 'lucide-react';
import { api, type SparkOutlineShape } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Spark course builder — Phase 5.
 *
 * Two-phase flow:
 *   1. Instructor types a one-sentence brief + optional audience hint.
 *      Click "Spark" → POST /ai/spark-course → preview pane shows the
 *      proposed module + lesson titles.
 *   2. Instructor accepts (or rejects + tries again). Accept materializes
 *      the rows: course → modules → lesson items, by chaining the existing
 *      REST endpoints. The lesson "fill-in" pass (real content blocks) is a
 *      follow-up commit per lesson — the structure is enough to demo and
 *      gives the instructor the satisfying "Phase 1 done in 8 seconds" feel.
 *
 * Simulate mode (AIML_API_KEY unset) returns a deterministic placeholder
 * outline — preview still renders so the flow can be exercised end-to-end
 * without billing wired up.
 */
export function SparkModal({ token, onClose }: { token: string; onClose: () => void }) {
  const navigate = useNavigate();
  const [brief, setBrief] = useState('');
  const [audience, setAudience] = useState('');
  const [outline, setOutline] = useState<SparkOutlineShape | null>(null);
  const [simulated, setSimulated] = useState(false);

  const sparkMutation = useMutation({
    mutationFn: () =>
      api.ai.sparkCourse(token, {
        brief: brief.trim(),
        ...(audience.trim() ? { audience: audience.trim() } : {}),
      }),
    onSuccess: (data) => {
      setOutline(data.outline);
      setSimulated(data.simulated);
    },
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      if (!outline) throw new Error('no_outline');
      // 1. Create the course.
      const slug = sanitizeSlug(outline.course.slug);
      const { course } = await api.courses.create(token, {
        slug,
        title: outline.course.title,
        description: outline.course.description,
      });

      // 2. For each module + each lesson, create them in order. We
      //    deliberately do these sequentially — Cloudflare D1 prefers small
      //    serial writes, and the user is staring at a progress indicator
      //    during this step anyway.
      for (const m of outline.modules) {
        const created = await api.modules.create(token, course.id, {
          title: m.title,
          ...(m.summary ? { description: m.summary } : {}),
        });
        for (const l of m.lessons) {
          await api.moduleItems.create(token, created.module.id, {
            type: 'lesson',
            title: l.title,
          });
        }
      }
      return course;
    },
    onSuccess: (course) => {
      navigate(`/c/${course.orgSlug}/${course.slug}`);
      onClose();
    },
  });

  const stage: 'brief' | 'preview' | 'accepting' = acceptMutation.isPending
    ? 'accepting'
    : outline
      ? 'preview'
      : 'brief';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-sm">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl">
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-accent" aria-hidden />
            <h2 className="font-display text-lg font-semibold">Spark a course</h2>
            {simulated && (
              <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                Simulated · set AIML_API_KEY
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {stage === 'brief' && (
          <BriefForm
            brief={brief}
            audience={audience}
            onBrief={setBrief}
            onAudience={setAudience}
            onSubmit={() => sparkMutation.mutateAsync()}
            isPending={sparkMutation.isPending}
            isError={sparkMutation.isError}
          />
        )}

        {stage === 'preview' && outline && (
          <PreviewPane
            outline={outline}
            onAccept={() => acceptMutation.mutateAsync()}
            onRedo={() => {
              setOutline(null);
              setSimulated(false);
            }}
            isError={acceptMutation.isError}
          />
        )}

        {stage === 'accepting' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-sm text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-accent" />
            Creating course → modules → lessons…
          </div>
        )}
      </div>
    </div>
  );
}

function BriefForm({
  brief,
  audience,
  onBrief,
  onAudience,
  onSubmit,
  isPending,
  isError,
}: {
  brief: string;
  audience: string;
  onBrief: (v: string) => void;
  onAudience: (v: string) => void;
  onSubmit: () => void;
  isPending: boolean;
  isError: boolean;
}) {
  return (
    <form
      className="flex flex-1 flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (!brief.trim() || isPending) return;
        onSubmit();
      }}
    >
      <div className="space-y-3 p-5">
        <label className="block">
          <span className="mb-1 block text-sm font-medium">One-sentence brief</span>
          <textarea
            autoFocus
            value={brief}
            onChange={(e) => onBrief(e.target.value)}
            rows={3}
            placeholder="e.g. Intro to high school biology — cells, genetics, ecosystems."
            className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">
            Audience <span className="text-muted-foreground">(optional)</span>
          </span>
          <input
            type="text"
            value={audience}
            onChange={(e) => onAudience(e.target.value)}
            placeholder="e.g. 9th grade · adult learners · upper-division HE"
            className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </label>
        <p className="text-xs text-muted-foreground">
          EduBorg drafts 4 modules with 3 lessons each. Costs 5 AI credits.
        </p>
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-border bg-card/50 px-5 py-3">
        {isError && (
          <span className="mr-auto text-xs text-destructive">
            Couldn't spark — try a more specific brief or try again.
          </span>
        )}
        <button
          type="submit"
          disabled={!brief.trim() || isPending}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          {isPending ? 'Sparking…' : 'Spark'}
        </button>
      </footer>
    </form>
  );
}

function PreviewPane({
  outline,
  onAccept,
  onRedo,
  isError,
}: {
  outline: SparkOutlineShape;
  onAccept: () => void;
  onRedo: () => void;
  isError: boolean;
}) {
  return (
    <>
      <div className="flex-1 overflow-y-auto p-5">
        <div className="mb-4 rounded-xl border border-accent/20 bg-accent/5 p-4">
          <h3 className="font-display text-base font-semibold">{outline.course.title}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{outline.course.description}</p>
          <div className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">
            URL slug: <span className="font-mono">{outline.course.slug}</span>
          </div>
        </div>
        <ol className="space-y-3">
          {outline.modules.map((m, i) => (
            <li key={i} className="rounded-xl border border-border bg-card p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Module {i + 1}
              </div>
              <div className="text-sm font-semibold">{m.title}</div>
              {m.summary && <div className="mt-0.5 text-xs text-muted-foreground">{m.summary}</div>}
              <ul className="mt-2 space-y-1">
                {m.lessons.map((l, j) => (
                  <li
                    key={j}
                    className={cn(
                      'rounded-md border border-border/60 bg-background px-2 py-1.5 text-xs',
                    )}
                  >
                    <div className="font-medium">{l.title}</div>
                    {l.summary && <div className="text-muted-foreground">{l.summary}</div>}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </div>
      <footer className="flex items-center justify-end gap-2 border-t border-border bg-card/50 px-5 py-3">
        {isError && (
          <span className="mr-auto text-xs text-destructive">
            Could not create course (slug may be taken). Try Redo.
          </span>
        )}
        <button
          type="button"
          onClick={onRedo}
          className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          Redo
        </button>
        <button
          type="button"
          onClick={onAccept}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Create this course
        </button>
      </footer>
    </>
  );
}

function sanitizeSlug(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 40) || 'new-course'
  );
}
